import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod";
import { toResult } from "#/modules/util.auth/api-errors";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import { doesSchoolSlugExistInMySchools } from "#/modules/generic.user/api.user";

/**
 * POST /user/select-school — remembers the school the user last visited (portal's
 * `lastSelectedSchoolMiddleware`). Fired from the `$school` layout loader on real navigations.
 * The slug is re-validated here against the user's own scholen (server fns are open endpoints,
 * and the loader-side check runs on the client during client navigations): a slug outside the
 * user's scholen — an admin browsing a foreign school, or a bogus value like the literal
 * "undefined" that hit the backend on main (devops 879) — never reaches the backend.
 */
/**
 * PUT /user/onboarding-seen — pressed on the onboarding page's CTA. One-way flip; the backend
 * invalidates the user-metadata cache, so the next login skips the onboarding guard.
 */
export const markOnboardingSeen = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async () => {
    // Fire-and-forget: a failed mark must not block leaving the onboarding page.
    toResult(await client.PUT("/user/onboarding-seen"));
    return null;
  });

export const rememberSelectedSchool = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ schoolSlug: z.string().min(1) }))
  .handler(async ({ data }) => {
    const metadataResult = toResult(await client.GET("/user/metadata"));
    if (Result.isError(metadataResult)) throw metadataResult.error;
    const metadata = metadataResult.value;

    if (
      metadata.lastSelectedSchoolSlug === data.schoolSlug ||
      !doesSchoolSlugExistInMySchools(metadata.scholen, data.schoolSlug)
    ) {
      return null;
    }

    // Fire-and-forget: a failed remember must not break the navigation.
    toResult(await client.POST("/user/select-school", { body: { schoolSlug: data.schoolSlug } }));
    return null;
  });
